import { memo } from 'react';

import { CSSProperties } from 'styled-components';

import { Badge } from '@components/badge';
import { IconCloseSmall } from '@components/icons/close-small';
import { useScopedTranslate } from '@locale/translate';
import { pxToRem } from '@utils/converters';

import { TravellerJourneyAction } from 'api/types';
import { ACTION_TYPES, getActionLabel } from 'ui/actions/action-config';
import { MoreItems, TravellerNodeActionCard } from 'ui/journey-builder/node-types/node-container';
import { NodeActions } from 'ui/traveller-journey-builder/node-types/common';
import { NodeContainer } from 'ui/traveller-journey-builder/node-types/node-container';
import { TravellerJourneyNode } from 'ui/traveller-journey-builder/node-types/types';

import { getConditionDescription, getConditionTooltip } from './description';
import { conditionGapSize } from './helpers';
import { useConditionDescription } from './hooks';

const ACTION_TYPE = ACTION_TYPES.condition_v2;

export const actionCardStyles: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: conditionGapSize,
};
export const tagStyles: CSSProperties = {
  display: 'flex',
  justifyContent: 'center',
  width: pxToRem(136),
  marginTop: pxToRem(8),
};

export const getDescription = (action: TravellerJourneyAction<typeof ACTION_TYPE>): string => {
  return getConditionDescription({ values: action.values, styled: false }) as string;
};

export const Node = memo((node: TravellerJourneyNode<typeof ACTION_TYPE>) => {
  const { t } = useScopedTranslate(nodeLocale);

  const values = node.data?.values;

  const { descriptionText, isTextClamped } = useConditionDescription(values);
  const description = node.data?.description?.trim();
  const hasDescription = Boolean(description);

  return (
    <NodeContainer node={node}>
      <TravellerNodeActionCard
        cardStyles={actionCardStyles}
        node={node}
        label={getActionLabel(ACTION_TYPE)}
        descriptionText={description || descriptionText}
        tooltip={!hasDescription ? getConditionTooltip({ values, isTextClamped }) : ''}
        renderBelowDescription={
          <>
            {!hasDescription && <MoreItems items={values?.conditions} />}
            {node.data.isHalted && node.data.isCompleted && (
              <Badge
                size="md"
                style={tagStyles}
                variant="indigo"
                icon={<IconCloseSmall />}
                text={t('conditionNotMet')}
              />
            )}
          </>
        }
      >
        <NodeActions node={node} />
      </TravellerNodeActionCard>
    </NodeContainer>
  );
});

const nodeLocale = {
  en: {
    conditionNotMet: 'Condition not met',
  },
};
